import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tasksAPI, Task } from '../api/api';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import TaskModal from './TaskModal';
import './MyTasks.css';
import './Modal.css';

const MyTasks: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'in_review' | 'completed'>('open');
  const [rejectionCommentTaskId, setRejectionCommentTaskId] = useState<number | null>(null);
  const [rejectionComment, setRejectionComment] = useState<string | null>(null);
  const [rejectionCommentLoading, setRejectionCommentLoading] = useState(false);

  const { user, isImpersonating } = useAuth();

  // Проверяем параметр task в URL
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (taskId) {
      const id = parseInt(taskId, 10);
      if (!isNaN(id)) {
        setSelectedTaskId(id);
        // Убираем параметр из URL
        setSearchParams({});
      }
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    loadTasks();
  }, [user?.id, isImpersonating]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const data = await tasksAPI.getMyTasks();
      setTasks(data);
    } catch (error) {
      console.error('Ошибка при загрузке задач:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskUpdated = () => {
    loadTasks();
    setSelectedTaskId(null);
  };

  const handleAcceptTask = async (taskId: number) => {
    try {
      await tasksAPI.acceptTask(taskId);
      // Обновляем задачу - перемещаем в столбец "В работе"
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, column_name: 'В работе', updated_at: new Date().toISOString() }
            : task
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.error || 'Ошибка при принятии задачи');
    }
  };

  const handleSendForReview = async (taskId: number) => {
    if (!window.confirm('Отправить задачу на проверку?')) {
      return;
    }

    try {
      await tasksAPI.sendForReview(taskId);
      // Обновляем только статус задачи в списке без полной перезагрузки
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: 'in_review' as const, updated_at: new Date().toISOString() }
            : task
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.error || 'Ошибка при отправке задачи на проверку');
    }
  };

  const handleRejectionBadgeClick = async (taskId: number) => {
    setRejectionCommentTaskId(taskId);
    setRejectionCommentLoading(true);
    setRejectionComment(null);

    try {
      // Загружаем детали задачи с комментариями
      const taskDetails = await tasksAPI.getById(taskId);
      
      if (taskDetails.comments && taskDetails.comments.length > 0) {
        // Находим последний комментарий от создателя задачи (руководителя)
        // Комментарии отсортированы по дате создания (старые первыми)
        const managerComments = taskDetails.comments.filter(
          (comment) => comment.user_id === taskDetails.created_by
        );
        
        if (managerComments.length > 0) {
          // Берем последний комментарий от руководителя
          const lastManagerComment = managerComments[managerComments.length - 1];
          setRejectionComment(lastManagerComment.content);
        } else {
          setRejectionComment('Комментарий не найден');
        }
      } else {
        setRejectionComment('Комментарий не найден');
      }
    } catch (error: any) {
      console.error('Ошибка при загрузке комментария:', error);
      setRejectionComment('Ошибка при загрузке комментария');
    } finally {
      setRejectionCommentLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return '#3498db';
      case 'in_review':
        return '#9b59b6';
      case 'completed':
        return '#27ae60';
      default:
        return '#95a5a6';
    }
  };

  const getStatusText = (status: string) => {
    // Убираем любые числа из статуса, если они там есть
    const cleanStatus = String(status).replace(/^\d+\s*/, '').trim();
    switch (cleanStatus) {
      case 'open':
        return 'Открыта';
      case 'in_review':
        return 'На проверке';
      case 'completed':
        return 'Завершена';
      default:
        return cleanStatus || 'Открыта';
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    return task.status === filter;
  });

  const isOverdue = (deadline: string | null | undefined) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Загрузка...</div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="my-tasks-container">
        <div className="my-tasks-header">
          <h1>Мои задачи</h1>
          <div className="header-actions">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="filter-select"
            >
              <option value="all">Все</option>
              <option value="open">Открытые</option>
              <option value="in_review">На проверке</option>
              <option value="completed">Завершенные</option>
            </select>
          </div>
        </div>

        <div className="tasks-list">
          {filteredTasks.length === 0 ? (
            <div className="empty-state">Нет задач</div>
          ) : (
            filteredTasks.map((task) => (
              <div key={task.id} className={`my-task-card ${task.is_rejected ? 'rejected-task' : ''}`}>
                <div className="task-card-header">
                  <h3 onClick={() => setSelectedTaskId(task.id)}>{task.title}</h3>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(String(task.status || '').replace(/^\d+\s*/, '').trim() || 'open') }}
                  >
                    {getStatusText(String(task.status || '').replace(/^\d+\s*/, '').trim() || 'open')}
                  </span>
                </div>
                {task.description && (
                  <p className="task-description">{task.description}</p>
                )}
                <div className="task-info">
                  {task.board_name && (
                    <span className="task-meta">📋 {task.board_name}</span>
                  )}
                  {task.status === 'completed' ? (
                    <span className="task-meta">📌 Завершено</span>
                  ) : task.column_name && (
                    <span className="task-meta">📌 {task.column_name}</span>
                  )}
                  {task.deadline ? (
                    <span
                      className={`task-deadline ${isOverdue(task.deadline) ? 'overdue' : ''}`}
                    >
                      ⏰ {new Date(task.deadline).toLocaleString('ru-RU')}
                      {isOverdue(task.deadline) && ' (Просрочено)'}
                    </span>
                  ) : (
                    <span className="task-deadline">
                      ⏰ Задача со свободным сроком
                    </span>
                  )}
                </div>
                <div className="task-actions">
                  {task.status === 'completed' && (
                    <div className="completed-status-info">
                      <span className="completed-status-text">✅ Задача завершена</span>
                      {task.updated_at && (
                        <span className="completed-status-date">
                          Завершена: {new Date(task.updated_at).toLocaleString('ru-RU', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                    </div>
                  )}
                  {task.status !== 'in_review' && task.status !== 'completed' && (
                    <div className="task-actions-row">
                      <button
                        onClick={() => setSelectedTaskId(task.id)}
                        className="view-btn task-btn-view"
                      >
                        Посмотреть
                      </button>
                      {/* Кнопка "Принять" показывается только для задач, которые еще не в столбце "В работе" */}
                      {task.status === 'open' && task.column_name !== 'В работе' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAcceptTask(task.id);
                          }}
                          className="accept-btn task-btn-accept"
                        >
                          Принять
                        </button>
                      )}
                      {task.status === 'open' && task.column_name === 'В работе' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSendForReview(task.id);
                          }}
                          className="review-btn task-btn-review"
                        >
                          Отправить на проверку
                        </button>
                      )}
                    </div>
                  )}
                  {task.status === 'in_review' && (
                    <div className="review-status-info">
                      <span className="review-status-text">⏳ Ожидает проверки руководителя</span>
                      {task.updated_at && (
                        <span className="review-status-date">
                          Отправлено: {new Date(task.updated_at).toLocaleString('ru-RU', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                    </div>
                  )}
                  {task.is_rejected ? (
                    <button
                      onClick={() => handleRejectionBadgeClick(task.id)}
                      className="reject-btn"
                      title="Нажмите, чтобы посмотреть комментарий руководителя"
                    >
                      Задача не прошла проверку
                    </button>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>

        {selectedTaskId && (
          <TaskModal
            taskId={selectedTaskId}
            onClose={() => {
              setSelectedTaskId(null);
            }}
            onTaskUpdated={loadTasks}
          />
        )}

        {/* Модальное окно с комментарием отклонения */}
        {rejectionCommentTaskId && (
          <div className="modal-overlay" onClick={() => setRejectionCommentTaskId(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Комментарий руководителя</h2>
                <button className="close-btn" onClick={() => setRejectionCommentTaskId(null)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                {rejectionCommentLoading ? (
                  <div className="loading">Загрузка комментария...</div>
                ) : (
                  <div className="rejection-comment">
                    <p>{rejectionComment || 'Комментарий не найден'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MyTasks;

